import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Check, X, Search, Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OPERATION_TYPE_OPTIONS, type SupplierOperationType } from "@/lib/supplierMaster";
import { listCustomers, upsertCustomer, deleteCustomer, normalizeNif, type CustomerMasterRecord } from "@/lib/customerMaster";
import { expandAccountShortcut, inferAccountLength } from "@/lib/accountShortcut";
import { useAuth } from "@/contexts/AuthContext";
import MasterImportDialog from "@/components/master/MasterImportDialog";
import MasterDuplicatesDialog from "@/components/master/MasterDuplicatesDialog";
import { findDuplicateGroups } from "@/lib/masterDuplicates";
import { Copy } from "lucide-react";

type CustomerRow = CustomerMasterRecord & { id: string };

interface CustomerMasterTabProps {
  companyId: string;
}

const CustomerMasterTab = ({ companyId }: CustomerMasterTabProps) => {
  const { isAdmin } = useAuth();
  const [companyName, setCompanyName] = useState("");
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFields, setEditFields] = useState<CustomerMasterRecord>({
    taxId: "",
    accountCode: "",
    salesAccount: "",
    description: "",
    operationType: "interior",
    postalCode: "",
  });
  const [adding, setAdding] = useState(false);
  const [newFields, setNewFields] = useState<CustomerMasterRecord>({
    taxId: "",
    accountCode: "",
    salesAccount: "",
    description: "",
    operationType: "interior",
    postalCode: "",
  });
  const { toast } = useToast();

  useEffect(() => {
    supabase.from("company_masters").select("name").eq("id", companyId).single()
      .then(({ data }) => { if (data) setCompanyName(data.name); });
  }, [companyId]);

  const fetchCustomers = async () => {
    setLoading(true);
    const rows = await listCustomers(companyId);
    setCustomers(rows);
    setLoading(false);
  };

  useEffect(() => { fetchCustomers(); }, [companyId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return customers;
    const q = search.toLowerCase();
    return customers.filter(
      (c) =>
        c.accountCode.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q) ||
        c.taxId.toLowerCase().includes(q) ||
        c.salesAccount.toLowerCase().includes(q)
    );
  }, [customers, search]);

  const dupGroups = useMemo(
    () => findDuplicateGroups(customers.map((c) => ({ id: c.id, taxId: c.taxId, account: c.accountCode, description: c.description }))),
    [customers],
  );

  const accountLength = useMemo(
    () => inferAccountLength(customers.flatMap((c) => [c.accountCode, c.salesAccount])),
    [customers]
  );
  const expandAcc = (v: string) => expandAccountShortcut(v, accountLength);

  const startEdit = (c: CustomerRow) => {
    setEditingId(c.id);
    setEditFields({
      taxId: c.taxId,
      accountCode: c.accountCode,
      salesAccount: c.salesAccount,
      description: c.description,
      operationType: c.operationType,
      postalCode: c.postalCode || "",
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const { error } = await upsertCustomer(companyId, { ...editFields, id: editingId });
    if (error) {
      toast({ title: "Error al guardar", description: error, variant: "destructive" });
    } else {
      setCustomers((prev) => prev.map((c) => c.id === editingId ? { ...c, ...editFields, taxId: normalizeNif(editFields.taxId) } : c));
      toast({ title: "Cliente actualizado" });
    }
    setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    const { error } = await deleteCustomer(id);
    if (error) {
      toast({ title: "Error al eliminar", description: error, variant: "destructive" });
    } else {
      setCustomers((prev) => prev.filter((c) => c.id !== id));
      toast({ title: "Cliente eliminado" });
    }
  };

  const handleAdd = async () => {
    if (!newFields.accountCode.trim() || !newFields.description.trim() || !newFields.taxId.trim()) {
      toast({ title: "Cuenta, Descripción y NIF son obligatorios", variant: "destructive" });
      return;
    }
    const { error } = await upsertCustomer(companyId, newFields);
    if (error) {
      toast({ title: "Error al añadir", description: error, variant: "destructive" });
    } else {
      setNewFields({ taxId: "", accountCode: "", salesAccount: "", description: "", operationType: "interior", postalCode: "" });
      setAdding(false);
      await fetchCustomers();
      toast({ title: "Cliente añadido" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por cuenta, nombre, NIF, cuenta de venta..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" onClick={() => setDupOpen(true)} className="gap-2 ml-auto">
          <Copy className="h-4 w-4" /> Duplicados por NIF ({dupGroups.length})
        </Button>
        <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2">
          <Upload className="h-4 w-4" /> Importar Excel
        </Button>
        <Button
          onClick={() => setAdding(true)}
          disabled={adding}
          className={`gap-2 bg-[hsl(var(--brand-green))] hover:bg-[hsl(152,40%,45%)] text-white`}
        >
          <Plus className="h-4 w-4" /> Añadir cliente
        </Button>
      </div>

      <MasterDuplicatesDialog
        open={dupOpen}
        onOpenChange={setDupOpen}
        table="customer_master"
        label="clientes"
        groups={dupGroups}
        onDone={fetchCustomers}
      />

      <MasterImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          kind="customers"
          companyId={companyId}
          companyName={companyName}
          accountLength={accountLength}
          onImported={fetchCustomers}
      />

      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs w-32">NIF/CIF</TableHead>
              <TableHead className="text-xs w-36">Cuenta cliente</TableHead>
              <TableHead className="text-xs">Descripción</TableHead>
              <TableHead className="text-xs w-32">Cuenta venta</TableHead>
              <TableHead className="text-xs w-24">Código postal</TableHead>
              <TableHead className="text-xs w-40">Tipo op.</TableHead>
              <TableHead className="text-xs w-24 text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {adding && (
              <TableRow className="bg-accent/30">
                <TableCell>
                  <Input value={newFields.taxId} onChange={(e) => setNewFields({ ...newFields, taxId: e.target.value })} placeholder="B12345678" className="h-7 text-xs font-mono" autoFocus />
                </TableCell>
                <TableCell>
                  <Input value={newFields.accountCode} onChange={(e) => setNewFields({ ...newFields, accountCode: e.target.value })} onBlur={(e) => setNewFields((f) => ({ ...f, accountCode: expandAcc(e.target.value) }))} placeholder="43000099" className="h-7 text-xs font-mono" />
                </TableCell>
                <TableCell>
                  <Input value={newFields.description} onChange={(e) => setNewFields({ ...newFields, description: e.target.value })} placeholder="Nombre del cliente" className="h-7 text-xs" />
                </TableCell>
                <TableCell>
                  <Input value={newFields.salesAccount} onChange={(e) => setNewFields({ ...newFields, salesAccount: e.target.value })} onBlur={(e) => setNewFields((f) => ({ ...f, salesAccount: expandAcc(e.target.value) }))} placeholder="70000000" className="h-7 text-xs font-mono" />
                </TableCell>
                <TableCell>
                  <Input value={newFields.postalCode} onChange={(e) => setNewFields({ ...newFields, postalCode: e.target.value.replace(/\D/g, "").slice(0, 5) })} placeholder="28001" className="h-7 text-xs font-mono" />
                </TableCell>
                <TableCell>
                  <Select value={newFields.operationType} onValueChange={(v) => setNewFields({ ...newFields, operationType: v as SupplierOperationType })}>
                    <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {OPERATION_TYPE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value} className="text-xs">{o.short}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setAdding(false)} className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><X className="h-4 w-4" /></button>
                    <button onClick={handleAdd} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-primary"><Check className="h-4 w-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">Cargando...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No se encontraron clientes</TableCell></TableRow>
            ) : filtered.map((c) => (
              <TableRow key={c.id} className="group">
                {editingId === c.id ? (
                  <>
                    <TableCell><Input value={editFields.taxId} onChange={(e) => setEditFields({ ...editFields, taxId: e.target.value })} className="h-7 text-xs font-mono" /></TableCell>
                    <TableCell><Input value={editFields.accountCode} onChange={(e) => setEditFields({ ...editFields, accountCode: e.target.value })} onBlur={(e) => setEditFields((f) => ({ ...f, accountCode: expandAcc(e.target.value) }))} className="h-7 text-xs font-mono" /></TableCell>
                    <TableCell><Input value={editFields.description} onChange={(e) => setEditFields({ ...editFields, description: e.target.value })} className="h-7 text-xs" /></TableCell>
                    <TableCell><Input value={editFields.salesAccount} onChange={(e) => setEditFields({ ...editFields, salesAccount: e.target.value })} onBlur={(e) => setEditFields((f) => ({ ...f, salesAccount: expandAcc(e.target.value) }))} className="h-7 text-xs font-mono" /></TableCell>
                    <TableCell><Input value={editFields.postalCode} onChange={(e) => setEditFields({ ...editFields, postalCode: e.target.value.replace(/\D/g, "").slice(0, 5) })} className="h-7 text-xs font-mono" /></TableCell>
                    <TableCell>
                      <Select value={editFields.operationType} onValueChange={(v) => setEditFields({ ...editFields, operationType: v as SupplierOperationType })}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {OPERATION_TYPE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value} className="text-xs">{o.short}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setEditingId(null)} className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><X className="h-4 w-4" /></button>
                        <button onClick={saveEdit} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-primary"><Check className="h-4 w-4" /></button>
                      </div>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell className="text-xs font-mono">{c.taxId}</TableCell>
                    <TableCell className="text-xs font-mono">{c.accountCode}</TableCell>
                    <TableCell className="text-xs">{c.description}</TableCell>
                    <TableCell className="text-xs font-mono">
                      {c.salesAccount ? (
                        <span className="inline-flex items-center rounded bg-[hsl(var(--brand-green))]/15 px-2 py-0.5 text-[hsl(var(--brand-green))] font-semibold">
                          {c.salesAccount}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-mono">{c.postalCode || <span className="text-muted-foreground/50">—</span>}</TableCell>
                    <TableCell className="text-xs">
                      {(() => {
                        const op = OPERATION_TYPE_OPTIONS.find((o) => o.value === (c.operationType || "interior"));
                        const isInterior = (c.operationType || "interior") === "interior";
                        return (
                          <span className={`inline-flex items-center rounded px-2 py-0.5 ${isInterior ? "bg-muted text-muted-foreground" : "bg-amber-500/15 text-amber-700 dark:text-amber-300 font-semibold"}`}>
                            {op?.short || "Interior"}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ opacity: 1 }}>
                        <button onClick={() => startEdit(c)} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => handleDelete(c.id)} className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">
        {filtered.length} cliente{filtered.length !== 1 ? "s" : ""} en {companyName || "esta empresa"}
      </p>
    </div>
  );
};

export default CustomerMasterTab;
